'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Clock, ChevronRight, Camera, User, ShieldCheck } from '@/lib/icons';
import { type Article, type ArticleBodyBlock, type AuthorProfile } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { parseDate, isVideoUrl, getCategoryColorClass, cn } from '@/lib/utils';
import { getCountryInfo } from '@/lib/countries';
import { ArticleBody } from '@/components/article-body';
import { ArticleSchema } from '@/components/seo/JsonLdSchema';
import { getArticleUrl, getCategoryUrl } from '@/lib/articles';
import { Separator } from '@/components/ui/separator';
import { ShareButton } from '@/components/share-button';
import { getCanonicalAuthorName } from '@/lib/authors';

interface ArticleViewProps {
  article: Article;
  relatedArticles: Article[];
  latestArticles: Article[];
  authorProfile?: AuthorProfile | null;
  tagCounts?: Record<string, number>;
}

function formatUtcDate(dateVal: any, formatType: 'dateOnly' | 'dateTime' | 'timeOnly' = 'dateOnly') {
  const date = parseDate(dateVal);
  if (formatType === 'dateTime') {
    const timeStr = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(date);
    const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
    return `${timeStr} • ${dateStr}`;
  }
  if (formatType === 'timeOnly') {
    return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function Breadcrumbs({ article }: { article: Article }) {
    const countryCode = article.country?.toLowerCase() || 'za';
    const countryInfo = getCountryInfo(countryCode);
    const categoryName = article.primaryCategory || 'News';
    
    const countryUrl = getCategoryUrl('Politics', undefined, countryCode);
    const categoryUrl = getCategoryUrl(categoryName);
    return (
        <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-6 overflow-x-auto whitespace-nowrap pb-2" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3 opacity-30" />
            <Link href={countryUrl} className="hover:text-primary transition-colors">{countryInfo.name}</Link>
            <ChevronRight className="h-3 w-3 opacity-30" />
            <Link href={categoryUrl} className="hover:text-primary transition-colors">{categoryName}</Link>
            <ChevronRight className="h-3 w-3 opacity-30" />
            <span className="text-foreground font-medium truncate max-w-[200px]" aria-current="page">
              {article.headline}
            </span>
        </nav>
    );
}

export function ArticleView({ article, relatedArticles, latestArticles, authorProfile, tagCounts }: ArticleViewProps) {
  const articleUrl = `https://newsafrika.com${getArticleUrl(article)}`;
  const authorSlug = article.authorProfileSlug || article.authorId || 'newsroom';
  const authorPath = `/authors/${authorSlug}`;
  
  const authorName = getCanonicalAuthorName(article, authorProfile);
  
  const pubDate = parseDate(article.publishDate);
  const modDate = parseDate(article.updatedAt);
  const hasSignificantUpdate = (modDate.getTime() - pubDate.getTime()) > (60 * 60 * 1000);
  const primaryImage = article.images?.[0];
  const bodyBlocks: ArticleBodyBlock[] = Array.isArray(article.body) ? [...article.body] : [];
  
  if (article.lede && !bodyBlocks.find(b => b.type === 'lede')) {
    bodyBlocks.unshift({ type: 'lede', text: article.lede });
  }

  const countryCode = article.country?.toLowerCase() || 'za';
  const countryInfo = getCountryInfo(countryCode);
  const countryName = countryInfo.name;
  const countryUrl = getCategoryUrl('Politics', undefined, countryCode);
  const categoryUrl = getCategoryUrl(article.primaryCategory || 'News');
  const breadcrumbItems = [
    { name: "Home", url: "https://newsafrika.com" },
    { name: countryName, url: countryUrl },
    { name: article.primaryCategory || "News", url: categoryUrl },
    { name: article.headline, url: articleUrl }
  ];

  const renderHeroMedia = () => {
    if (primaryImage?.src && isVideoUrl(primaryImage.src)) {
      return (
        <div className="mb-10 max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-xl bg-black border border-white/10">
          <video 
            controls 
            className="w-full h-auto" 
            playsInline 
            preload="metadata"
            poster={article.images.find(i => !isVideoUrl(i.src))?.src || article.video?.thumbnail}
          >
            <source src={primaryImage.src} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }
    if (primaryImage?.src) {
      return (
        <div className="mb-10 max-w-2xl mx-auto relative aspect-[4/3] md:aspect-video overflow-hidden rounded-2xl shadow-xl bg-muted">
          <Image 
            src={primaryImage.src} 
            alt={primaryImage.alt || article.headline} 
            fill 
            className="object-cover" 
            priority 
            fetchPriority="high"
            sizes="(max-width: 768px) 100vw, 800px"
            quality={75}
          />
          {(primaryImage.caption || primaryImage.credit) && (
            <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/90 via-black/40 to-transparent text-white pointer-events-none">
              {primaryImage.caption && (
                <div className="text-sm md:text-base font-medium mb-1.5 leading-tight">
                  {primaryImage.caption}
                </div>
              )}
              {primaryImage.credit && (
                <div className="flex items-center gap-1.5 text-[10px] md:text-xs opacity-80 font-bold uppercase tracking-wider">
                  <Camera className="h-3.5 w-3.5" />
                  <span>: {primaryImage.credit}</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-background text-foreground">
      <ArticleSchema 
        article={article} 
        url={articleUrl} 
        breadcrumbItems={breadcrumbItems} 
        authorProfile={authorProfile}
      />
      
      <div className="container mx-auto max-w-5xl px-4 py-8 md:py-12">
        <Breadcrumbs article={article} />
        <div className="lg:flex lg:gap-12">
          <div className="lg:flex-1">
            <article>
              <header className="mb-8">
                <div className="mb-4 flex items-center flex-wrap gap-3">
                    <Link 
                      href={getCategoryUrl(article.primaryCategory)}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/5 border transition-all hover:bg-primary/10",
                        getCategoryColorClass(article.primaryCategory)
                      )}
                    >
                      {article.subcategory || article.primaryCategory || 'General'}
                    </Link>

                    {article.article === 'Sponsored' && (
                      <span className="px-3 py-1 bg-white text-black rounded-full text-[10px] font-black uppercase tracking-widest border border-white shadow-sm">
                        Sponsored
                      </span>
                    )}
                </div>
                
                <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-4 tracking-tight article-headline">{article.headline}</h1>
                {article.subheadline && (
                  <p className="text-xl text-muted-foreground mb-6 leading-snug font-medium article-lede">
                    {article.subheadline}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm md:text-xs mb-8 md:mb-6">
                  <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                    <Link 
                      href={authorPath} 
                      rel="author"
                      className="hover:text-primary transition-colors font-bold text-foreground inline-flex items-center gap-1"
                      prefetch={false}
                    >
                      <User className="h-3 w-3" /> By: {authorName}
                    </Link>
                    <span className="opacity-30 mx-1">|</span>
                    <time dateTime={pubDate.toISOString()} className="font-medium">
                      {formatUtcDate(article.publishDate)}
                    </time>
                  </div>
                </div>
              </header>
              {renderHeroMedia()}
              
              <div className="max-w-5xl mx-auto lg:mx-0">
                {article.highlights && article.highlights.length > 0 && (
                  <div className="mb-10">
                    <ul className="space-y-3">
                      {article.highlights.map((highlight, i) => (
                        <li key={i} className="flex gap-3 text-lg leading-relaxed font-medium">
                          <span className="text-primary font-bold text-xl">•</span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-10 border-b border-foreground/20" />
                  </div>
                )}
                <ArticleBody body={bodyBlocks} />
                {hasSignificantUpdate && (
                  <div className="mt-16 mb-12">
                     <Separator className="bg-foreground/20 mb-6" />
                     <div className="flex flex-col gap-1">
                        <div className="text-[10px] text-muted-foreground tracking-widest font-black">
                          Modified at: <time dateTime={modDate.toISOString()}>{formatUtcDate(article.updatedAt, 'dateTime')}</time>
                        </div>
                        <div className="text-[9px] text-zinc-600 italic">
                          Editorial Integrity: Updates reflect corrections or significant developments since publication.
                        </div>
                     </div>
                  </div>
                )}
                {article.sources && article.sources.length > 0 && (
                  <div className="mt-16 border-t border-foreground/20 pt-10">
                      <h2 className="text-xl font-bold mb-6 flex items-center gap-3 font-headline">
                        <span className="h-6 w-1 bg-primary"></span>Official Evidence & Sources
                      </h2>
                      <ul className="space-y-3 list-none">
                        {article.sources.map((source, i) => (
                          <li key={i} className="group">
                            <a href={source.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-foreground/10 hover:border-primary/30 transition-all">
                              <span className="text-sm font-bold group-hover:text-primary transition-colors underline underline-offset-4">{source.title}</span>
                              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Primary Record</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                  </div>
                )}
                {article.tags && article.tags.length > 0 && (
                  <div className="mt-16 border-t border-foreground/20 pt-10">
                      <h2 className="text-xl font-bold mb-6 flex items-center gap-3 font-headline">
                        <span className="h-6 w-1 bg-primary"></span>Report Topics
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag, i) => {
                          const topicSlug = tag.toLowerCase().trim().replace(/\s+/g, '-');
                          const count = tagCounts?.[tag] || 0;
                          const isEstablished = count > 30;
                          if (isEstablished) {
                            return (
                              <Link key={i} href={`/topics/${topicSlug}`}>
                                <Badge variant="outline" className="rounded-full px-4 py-1.5 transition-all text-sm font-medium hover:border-primary hover:text-primary hover:bg-primary/5 cursor-pointer border-foreground/60">
                                  {tag}
                                </Badge>
                              </Link>
                            );
                          }
                          return (
                            <Badge 
                              key={i} 
                              variant="outline" 
                              className="rounded-full px-4 py-1.5 text-sm font-medium opacity-60 cursor-default border-dashed border-foreground/60"
                              title="Established hub verification in progress..."
                            >
                              {tag}
                            </Badge>
                          );
                        })}
                      </div>
                  </div>
                )}
              </div>
            </article>
          </div>
          <aside className="lg:w-64 mt-12 lg:mt-0 -mx-4 md:mx-0">
            <div className="sticky top-24 space-y-12">
              {latestArticles.length > 0 && (
                <div className="rounded-3xl border border-foreground/20 overflow-hidden">
                    <div className="bg-white/[0.03] p-6 border-b border-foreground/20">             
                      <h2 className="text-sm font-bold flex items-center gap-2 font-headline text-primary uppercase tracking-widest">
                        Latest from Newsroom
                      </h2>
                   </div>
                   <div className="divide-y divide-foreground/10">
                      {latestArticles.map(a => {
                        const img = a.images?.[0];
                        const aPath = getArticleUrl(a);
                        const authPath = `/authors/${a.authorProfileSlug || a.authorId || 'newsroom'}`;
                        const sidebarAuthorName = getCanonicalAuthorName(a);
                        
                        return (
                          <div key={a.id} className="p-5 hover:bg-white/[0.02] transition-colors group relative">
                            {img?.src && (
                              <Link 
                                href={aPath} 
                                className="block mb-3 overflow-hidden rounded-xl aspect-video relative bg-muted shadow-sm"
                                prefetch={false}
                                aria-label={a.headline}
                              >
                                <Image 
                                  src={img.src} 
                                  alt="" 
                                  fill 
                                  className="object-cover group-hover:scale-105 transition-transform duration-500" 
                                  sizes="200px"
                                  quality={60}
                                />
                              </Link>
                            )}
                            <div className="space-y-3">
                              <Link href={aPath} className="block group/link" prefetch={false}>
                                <h4 className="text-sm font-semibold leading-snug group/link:text-primary transition-colors line-clamp-4 mb-2">{a.headline}</h4>
                              </Link>
                              <div className="flex items-center justify-between pt-2">
                                <div className="text-[10px] text-muted-foreground font-medium flex flex-col gap-0.5">
                                   <Link href={authPath} className="hover:text-primary transition-colors" prefetch={false}>By: {sidebarAuthorName}</Link>
                                   <span className="opacity-60">
                                     {formatUtcDate(a.publishDate)}
                                   </span>
                                </div>
                                <ShareButton article={a} className="h-7 w-7 text-foreground/70 hover:text-primary transition-colors" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                   </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
